import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Lock } from 'lucide-react';

interface ArticleCardProps {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string | null;
  publishedAt: string;
  readingTime: number;
  featuredImage: string | null;
  isPremium: boolean;
}

export function ArticleCard({
  slug,
  title,
  excerpt,
  category,
  publishedAt,
  readingTime,
  featuredImage,
  isPremium,
}: ArticleCardProps) {
  const { locale } = useParams<{ locale: string }>();
  const { t, i18n } = useTranslation('blog');
  const currentLocale = locale || i18n.language || 'fr';

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group h-full flex flex-col">
      <Link to={`/${currentLocale}/blog/${slug}`} className="flex flex-col h-full">
        {/* Image */}
        <div className="h-48 bg-gradient-to-br from-aspexit-green/20 to-aspexit-orange/20 flex items-center justify-center relative">
          {featuredImage ? (
            <img
              src={featuredImage}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-aspexit-gray">Image</span>
          )}
          {isPremium && (
            <div className="absolute top-2 right-2 bg-aspexit-purple text-white px-2 py-1 rounded-md flex items-center gap-1 text-xs">
              <Lock className="w-3 h-3" />
              Premium
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 flex-1 flex flex-col">
          <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-3 group-hover:text-aspexit-green transition-colors line-clamp-2">
            {title}
          </h3>

          <p className="text-sm text-aspexit-gray mb-4 line-clamp-3 flex-1">
            {excerpt?.replace(/^Partagez cet article\s*!\s*/i, '')}
          </p>

          {/* Meta */}
          <div className="flex items-center justify-between text-sm text-aspexit-gray pt-4 border-t border-border">
            <span>
              {new Date(publishedAt).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {readingTime} min
            </span>
          </div>

          <div className="mt-4">
            <span className="text-aspexit-green text-sm font-medium group-hover:underline">
              {t('readMore')}
            </span>
          </div>
        </div>
      </Link>
    </Card>
  );
}
